import {Component, OnInit, OnDestroy, Input, QueryList, ElementRef, TemplateRef} from '@angular/core';
import { MDCTabBar } from '@material/tabs';
import { CustomizationService } from './customization.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ITag } from '../../models/tag';
import { ITagInfo } from '../../models/taginfo';
import { findIndex, propEq, last, assoc, omit, contains, isEmpty, countBy } from 'ramda/es';
import 'rxjs/add/observable/fromEvent';
import { ISession } from '../../models/session';
import { AuthService } from '../auth/auth.service';
import { IAction } from '../tabs/tabs.component';
import { first, flatMap, map } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import 'rxjs/add/observable/of';
import { ILink } from '../../models/link';
import { PreviewService } from '../preview/preview.service';
import { PluginsService } from '../plugins/plugins.service';
import { IPlugin } from '../../models/plugin';
import { SessionsService } from '../sessions/sessions.service';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/distinctUntilChanged';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';
import { addIndex, equals, reduce } from 'ramda';

const SessionTypes = {
  video: 'video',
  interactive: 'interactive',
  mixte: 'mixte',
  vod: 'vod'
};

const tagsToDisplay = [
  'webevent.global.guest.name',
  'webevent.global.presentation.content.htmlsupport',
  'webevent.global.description.content',
  'webevent.global.date',
  'webevent.global.hour',
  'webevent.global.guest.bio.content',
  'webevent.global.presentation.content',
  'wall.00global.smsphonenumber',
  'wall.00global.shortlink'
];

const wallInstructionsTagsToDisplay = ['wall.00global.smsphonenumber', 'wall.00global.shortlink'];

@Component({
  selector: 'customization',
  templateUrl: './customization.component.html',
  styleUrls: ['./customization.component.scss']
})
export class CustomizationComponent implements OnInit, OnDestroy {

  @Input('session') session: ISession;
  @Input('type') type: string;
  public tabs = [];
  public completions = [];
  public wallInstructionsCompletions = [];
  public smsPluginActive = false;
  public text = '';
  public tags: ITag[];
  public beforeUpdateTags: ITag[];
  public selectedTag: ITag;
  public updateAction: IAction = {
    name: 'tags.edit.update',
    color: 'primary',
    exec: () => this.updateAll()
  };
  public previewHash;
  public links: ILink[] = [];
  public uploadFileError = '';
  public updateUploadFilename$: Subject<{ITag, number, string}> = new Subject;
  public updateAllDebounced$: Subject<void> = new Subject();
  private modalRef: BsModalRef;
  private modalSelectedTag: ITag;
  private fileIndex = -1;
  private maxUploadedFiles = 1;
  private langChange$: any;

  constructor(
    private customizationService: CustomizationService,
    private translateService: TranslateService,
    public authService: AuthService,
    private pluginsService: PluginsService,
    private previewService: PreviewService,
    private sessionService: SessionsService,
    private modalService: BsModalService
  ) { }

  openModal(template: TemplateRef<any>, tag: ITag, index: number = -1) {
    this.modalSelectedTag = tag;
    this.fileIndex = index;
    this.modalRef = this.modalService.show(template, { class: 'inmodal' });
  }

  disableCaching(data: string) {
    if (!data) return data;
    const disableCache = 'no_cache=' + this.previewHash;
    const separator = data.indexOf('?') === -1 ? '?' : '&';
    return data.concat(separator, disableCache);
  }

  private removeImageOrigin(imageUrl: string) {
    try {
      const { origin } = new URL(imageUrl);
      return imageUrl.replace(origin, '');
    } catch (e) {
      return imageUrl;
    }
  }

  private uploadImages(tags: ITag[]): Observable<ITag[]> {
    // keep only tags of which we need to upload the image
    const tagsWithImageToUpload = tags.filter(tag => tag.type === 'image' && tag.value.data);
    return Observable.of(tagsWithImageToUpload).pipe(
      flatMap((tags: ITag[]) => {
        if (isEmpty(tags)) {
          // forkJoin doesn't .next() on empty array
          return Observable.of([]);
        }
        const tags$ = tags.map(tag => {
          return this.customizationService.uploadImage(
            this.session,
            omit(['title'], tag),
            tag.value.data
          );
        });
        // upload each image
        return forkJoin(...tags$);
      }),
      map(updatedImageTags => {
        // replace tag value by image url in image tags
        return tags.map(tag => {
          const updatedImageTag = updatedImageTags.find(t => t.name === tag.name);
          if (updatedImageTag) {
            tag.name = updatedImageTag.name;
            tag.value = this.removeImageOrigin(updatedImageTag.value);
          }
          return tag;
        });
      })
    );
  }

  ngOnInit() {
    this.tabs = [
      { name: 'features.webevent', data: 'webevent', models: ['video', 'mixte', 'vod'] },
      { name: 'features.webapp', data: 'webapp', models: ['interactive', 'mixte'] },
      { name: 'features.wall', data: 'wall', models: ['interactive', 'mixte'] }
    ].filter(tab => contains(this.session.types.sessionType, tab.models));

    this.onTabChange(this.tabs[0]);

    const getTags$ = this.customizationService
      .getCustomizableFields(this.session, this.type)
      .map((tags: ITag[]) =>
        tags.map((tag: ITag) => {
          return assoc('title', last(tag.description.split('--')), tag);
        })
      );
    const getPlugins$ = this.pluginsService.getAll(this.session);

    forkJoin(getTags$, getPlugins$).subscribe(
      ([tags, plugins]: [ITag[], IPlugin[]]) => {
        const smsPlugin = plugins.find(plugin => plugin.name === 'sms');
        this.smsPluginActive = !!(smsPlugin && smsPlugin.isActivated);
        const uploadedFilesPlugin = plugins.find(tag => tag.name === 'uploaded_files');
        const showUploadedFiles = uploadedFilesPlugin && uploadedFilesPlugin.isActivated;
        const teasingPluginsCount = countBy((plugin) => (plugin.category === 'teasing' && plugin.isActivated), plugins);
        const teasingFeaturesSelected = teasingPluginsCount.true && teasingPluginsCount.true > 0 ? true : false;
        this.tags = tags
          .map(tag => {
            return tag.name.indexOf('wall') >= 0 && tag.type === 'text'
              ? assoc('type', 'select', tag)
              : tag;
          })
          .filter(tag => {
            // Filter: get only tags which are in a activated wall (walls are activated by plugins)
            return tag.type !== 'select'
              ? true
              : plugins.find(plugin =>
                plugin.activate.some(el => el.name === tag.name && el.for === 'tag')
              ).isActivated;
          })
          .filter(tag => {
            // Filter for guest mode: show the wall 01 if guestMode is false otherwise show 05
            return ![
              {
                name: `wall.01.display.instructions`,
                guestMode: true
              },
              {
                name: `wall.05.display.instructions`,
                guestMode: false
              }
            ].some(
              filter =>
                tag.name === filter.name &&
                this.sessionService.guestMode$.value === filter.guestMode
            );
          }).filter(tag => {
            return !(tag.name === 'webevent.global.uploaded_files' && !showUploadedFiles)
          }).filter(tag => {
            return !(tag.name === 'webevent.global.beforelivecontent' && teasingFeaturesSelected)
          });
        this.beforeUpdateTags = JSON.parse(JSON.stringify(tags));
        this.previewHash = new Date().getTime();
        this.syncUploadedFiles(this.tags.find(tag => tag.name === 'webevent.global.uploaded_files'));
        this.loadCompletions();
      },
      err => console.error(err)
    );

    this.langChange$ = this.translateService.onLangChange.subscribe(() => {
      this.loadCompletions();
    });

    this.updateUploadFilename$
      .debounceTime(2000)
      .distinctUntilChanged()
      .subscribe(({ITag: tag, number: index, string: display}) => {
        const value = {
          id: tag.value[index].id,
          name: tag.value[index].name,
          alias: tag.value[index].alias,
          display
        };
        this.customizationService.updateFile(this.session, value)
          .subscribe(val => {
            tag.value[index] = val;
            this.updateAll()
          }, err => {
            toastr.error(this.translateService.instant('action.failed'));
          });
      });
    this.customizationService.getSessionConfig().subscribe(config => {
      this.maxUploadedFiles = config.maxUploadedFiles || 1;
    });

    this.updateAllDebounced$
      .debounceTime(500)
      .subscribe(() => this.updateAll());
  }

  ngOnDestroy() {
    if (this.langChange$) {
      this.langChange$.unsubscribe();
    }
  }

  toggleColorPicker(name, show = true) {
    const container = jQuery('#' + name.split('.').join('') + ' .color-container');
    if (show && !container.hasClass('color-container-open')) {
      return container.addClass('color-container-open');
    }
    container.removeClass('color-container-open');
  }

  isBackgroundImage(name) {
    return contains('background.image', name);
  }

  removeBackgroundImage(tag) {
    this.customizationService.removeImage(
      this.session,
      omit(['title'], tag)
    ).subscribe(() => {
      this.beforeUpdateTags = JSON.parse(JSON.stringify(this.tags));
      this.previewHash = new Date().getTime();
      tag.value = '';
    });
  }

  loadCompletions() {
    this.translateService.get('tags.raw.(!--ct:account.id--)').subscribe(description => {
      this.customizationService.getTags(this.session).subscribe(
        (tags: any[]) => {
          const withLabel = (tag: any) => {
            const key = 'tags.edit.display.' + tag.name;
            const translated = this.translateService.instant(key);
            const label = translated !== key ? translated : null;
            return { ...tag, ...(!tag.label && label && { label }) };
          };
          this.wallInstructionsCompletions = tags
            .filter(tag => wallInstructionsTagsToDisplay.indexOf(tag.name) >= 0)
            .filter(tag => tag.name !== 'wall.00global.smsphonenumber' || this.smsPluginActive)
            .map(withLabel);
          this.completions = tags
            .filter(tag => tagsToDisplay.indexOf(tag.name) >= 0)
            .filter(tag => tag.name !== 'wall.00global.smsphonenumber' || this.smsPluginActive)
            .concat({
              name: 'accountid',
              type: 'text',
              value: this.authService.user.name,
              description,
              label: description,
              tag: '(!--accountid--)'
            }).map(tag => ({ ...withLabel(tag), type: 'session'}));
        },
        err => toastr.error(this.translateService.instant('action.failed'))
      );
    });
  }

  public setSocialHref(tag, name, href) {
    tag.value = tag.value.map(network => {
      if (network.name === name) {
        network.href = href;
      }
      return network;
    });
  }

  public updateFilename(tag: ITag, index: number, name: string) {
    this.updateUploadFilename$.next({ITag: tag, number: index, string: name});
  }

  public uploadFile(tag, $event, index: number = -1) {
    this.uploadFileError = '';
    const file: File = $event.target.files[0];
    if (!file) {
      return;
    }
    // allow images below 5Mb
    if (file.size >= 5242880) {
      return (this.uploadFileError = this.translateService.instant('action.fileBig'));
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    const onLoadEnd = (next: ProgressEvent) => {
      const target: any = next.target;
      const b64image: string = target.result;
      return {name: file.name, data: b64image}
    };
    const fileReader$ = fromEvent(reader, 'loadend');
    fileReader$.pipe(first(), map(onLoadEnd), flatMap((upload): any => {
      const value = {
        id: tag.value[index].id,
        name: upload.name,
        display: upload.name,
        alias: `file${index + 1}`,
      };
      this.customizationService.uploadFile(this.session, value, upload.data)
        .subscribe(val => {
          tag.value[index] = val;
          this.updateAll();
        })
    })).subscribe();
  }

  public removeFile(tag: ITag, index: number) {
    this.uploadFileError = '';
    const fileToRemove  = (tag.value as any[])[index];
    this.customizationService.removeFile(this.session, fileToRemove)
      .subscribe(() => {
        // compact
        tag.value.splice(index, 1);
        if (tag.value.length === 0) {
          // keep one waiting upload slot
          tag.value.push({});
        }
        const filesToUpdate = addIndex(reduce)((acc, file: any, i: number) => {
          if (file.id && file.alias !== `file${i + 1}`) {
            file.alias = `file${i + 1}`;
            return [ ...acc as any[], this.customizationService.updateFile(this.session, { ...file }) ];
          }
          return acc;
        }, [])(tag.value);
        forkJoin(...filesToUpdate as any[]).subscribe(() => {
          this.updateAll(false);
        });
        this.updateAll();
      }, err => {
        toastr.error(this.translateService.instant('action.failed'));
      })
  }

  public updateAll(notify: boolean = true) {
    const tags = this.tags.filter((tag, i) => tag.value !== this.beforeUpdateTags[i].value);
    this.uploadImages(tags)
      .pipe(
        flatMap(tagsWithImage => this.customizationService.updateTags(this.session, tagsWithImage))
      )
      .subscribe(
        () => {
          if (notify) {
            toastr.success(this.translateService.instant('action.success'));
          }
          this.beforeUpdateTags = JSON.parse(JSON.stringify(this.tags));
          this.previewHash = new Date().getTime();
          this.sessionService.setComplete(this.session, 'ui');
        },
        error => {
          if (error.status === 413) {
            return toastr.error(this.translateService.instant('action.imageBig'));
          }
          toastr.error(this.translateService.instant('action.failed'));
        }
      );
  }

  public onTabChange(tab) {
    this.pluginsService.getAll(this.session).subscribe(plugins => {
      this.links = this.previewService.getLinks(tab.data, this.session, plugins);
    });
  }

  public infoForTag(tag: ITag): ITagInfo {
    if (tag.name === 'webevent.global.uploaded_files') {
      return {
        title: 'tags.edit.display.webevent.global.uploaded_files.info.title',
        description: 'tags.edit.display.webevent.global.uploaded_files.info.description'
      }
    }
    return null
  }

  public displayImageOrIframe(tag: ITag): boolean {
    return tag.type === 'group' && tag.name === 'webevent.global.beforelivecontent';
  }

  public canAddFile(tag: ITag): boolean {
    const files = tag.value as any[];
    const waitForUpload = files.some(file => !file.id);
    return files.length < this.maxUploadedFiles && !waitForUpload;
  }

  public handleUploaderClick(index: number) {
    const uploader: HTMLElement = document.getElementById('uploader_' + index);
    if (uploader) {
      uploader.click();
    }
  }

  private syncUploadedFiles(tag: ITag | undefined): void{
    this.customizationService.getFiles(this.session).subscribe(files => {
      if (tag) {
        if (!equals(tag.value, files)) {
          tag.value = files || [];
          this.updateAll(false);
        }
        if (tag.value.length === 0) {
          // keep one waiting upload slot
          tag.value.push({});
        }
      }
    })
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/customization/customization.component.ts